import { getGachaEnvironment } from "@anaconda/core/gacha/evm-chains"
import {
  type GachaEvmPurchase,
  gachaEvmPurchaseSchema,
} from "@anaconda/core/gacha/evm-purchase"
import { wrap } from "@reatom/core"
import { type GachaApiOptions, getGachaApiUrl } from "./gacha-api"
import { requestGachaEvmBalanceRefresh } from "./gacha-evm-payment-balance"
import {
  gachaEvmPurchases,
  requestGachaEvmOutcomeRefresh,
} from "./gacha-evm-purchase-state"
import { getGachaIdentityToken } from "./gacha-identity-token"

export async function continueGachaEvmOpening(
  record: GachaEvmPurchase,
  options: GachaApiOptions,
) {
  if (
    getGachaEnvironment(options.collectorCryptGachaBaseUrl ?? "") !==
    record.environment
  )
    throw new Error("Original purchase environment mismatch")
  const token = await wrap(
    getGachaIdentityToken({
      userId: record.userId,
      walletAddress: record.walletAddress,
    }),
  )
  if (!token) throw new Error("Verified identity token required")
  const response = await wrap(
    fetch(
      getGachaApiUrl(
        `/evm/purchases/${encodeURIComponent(record.id)}/open`,
        options,
      ),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ walletAddress: record.walletAddress }),
        signal: AbortSignal.timeout(30_000),
      },
    ),
  )
  if (!response.ok)
    throw new Error("Unable to continue the original pack. Retry later.")
  const updated = gachaEvmPurchaseSchema.parse(await wrap(response.json()))
  if (
    updated.id !== record.id ||
    updated.userId !== record.userId ||
    updated.memo !== record.memo ||
    updated.machineCode !== record.machineCode ||
    updated.environment !== record.environment ||
    updated.upstream !== record.upstream ||
    updated.chain.chainId !== record.chain.chainId ||
    updated.chain.cardContract.toLowerCase() !==
      record.chain.cardContract.toLowerCase() ||
    updated.walletAddress.toLowerCase() !== record.walletAddress.toLowerCase()
  )
    throw new Error("Original purchase identity mismatch")
  gachaEvmPurchases.upsert(updated)
  const observed =
    gachaEvmPurchases.records().find((item) => item.id === updated.id) ??
    updated
  if (!observed.active) {
    requestGachaEvmBalanceRefresh()
    requestGachaEvmOutcomeRefresh(observed)
  }
  return observed
}
